import React from "react";
import { Badge } from "@/components/ui/badge";
import { Lock } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

export type PlanType = 'free' | 'premium';

interface PlanBadgeProps {
  requiredPlan: PlanType;
  userPlan: PlanType;
  tooltip?: string;
  showFullText?: boolean;
}

const PlanBadge: React.FC<PlanBadgeProps> = ({ 
  requiredPlan, 
  userPlan, 
  tooltip,
  showFullText = false 
}) => {
  // Convert plan to numeric value for comparison
  const planValues = { free: 0, premium: 1 };
  const userPlanValue = planValues[userPlan];
  const requiredPlanValue = planValues[requiredPlan];
  
  // Check if user has access to this feature
  const hasAccess = userPlanValue >= requiredPlanValue;

  // Set badge color and style based on required plan
  let badgeColor = 'bg-gray-200 text-gray-600';
  let badgeIcon = <></>;
  
  if (requiredPlan === 'premium') {
    badgeColor = hasAccess 
      ? 'bg-sauna-orange text-white' 
      : 'bg-sauna-orange/10 text-sauna-orange border border-sauna-orange/20';
    
    if (!hasAccess) {
      badgeIcon = <Lock size={12} className="mr-1" />;
    }
  }

  const badgeText = showFullText 
    ? `${requiredPlan.charAt(0).toUpperCase() + requiredPlan.slice(1)} Plan` 
    : requiredPlan.charAt(0).toUpperCase() + requiredPlan.slice(1);

  // If tooltip is provided, wrap badge in tooltip
  if (tooltip) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge className={`text-xs py-0.5 px-1.5 ${badgeColor} cursor-help`}>
              {badgeIcon}
              {badgeText}
            </Badge>
          </TooltipTrigger>
          <TooltipContent>
            <p className="text-xs">{tooltip}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // Return regular badge if no tooltip
  return (
    <Badge className={`text-xs py-0.5 px-1.5 ${badgeColor}`}>
      {badgeIcon}
      {badgeText}
    </Badge>
  );
};

export default PlanBadge;
